import { Routes } from '@angular/router';
import { authGuard } from './auth/guards/auth.guard';
import { dashboardAccessGuard } from './shared/guards/dashboard-access.guard';
import { featureFlagGuard } from './shared/guards/feature-flag.guard';
import { isIndagoAiTenantGuard } from './shared/guards/indago-ai-tenant.guard';
import { NotFoundComponent } from './shared/components/not-found/not-found.component';
import { FeatureFlagType } from './shared/models/config/feature-flag.type';
import { permissionGuard } from './shared/guards/permission.guard';

export const routes: Routes = [
  {
    // Must stay a routed component, not a `redirectTo`: the connector OAuth return
    // lands on `/?connectorAuth=…&connectorId=…` and a redirect drops the query params.
    // Must stay routed and ahead of the shell route.
    path: '',
    pathMatch: 'full',
    canActivate: [dashboardAccessGuard],
    loadComponent: () =>
      import('./connectors/components/connector-auth-landing/connector-auth-landing.component').then(
        (it) => it.ConnectorAuthLandingComponent
      ),
  },
  {
    path: 'auth',
    loadComponent: () => import('./auth/auth.component').then((it) => it.AuthComponent),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'sign-in', pathMatch: 'full' },
      {
        path: 'sign-in',
        loadComponent: () => import('./auth/components/login/login.component').then((it) => it.LoginComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () => import('./auth/components/reset-password/reset-password.component').then((it) => it.ResetPasswordComponent),
      },
    ],
  },
  {
    // One pathless shell for every signed-in section, so the rail, its sockets and its dialogs
    // survive a move between Workspaces, Vaults and Administration.
    path: '',
    canActivate: [dashboardAccessGuard],
    loadComponent: () => import('./layout/workspaces-shell/workspaces-shell.component').then((it) => it.WorkspacesShellComponent),
    children: [
      {
        path: 'administration-panel',
        loadComponent: () => import('./admin/admin-panel.component').then((it) => it.AdminPanelComponent),
        canActivate: [dashboardAccessGuard, featureFlagGuard],
        data: {
          featureFlag: FeatureFlagType.PLATFORM_USAGE_PAGE_ENABLED,
        },
        // isIndagoAiTenantGuard on the children below only keeps these pages out of the UI for
        // non-indagoai tenants; it stops a navigation, not a request. The tenant-scoping of the
        // data they render is enforced by the APIs behind them, which is where it has to hold.
        children: [
          {
            path: '',
            redirectTo: 'platform-usage',
            pathMatch: 'full',
          },
          { path: 'llm-costs', redirectTo: 'platform-usage' },
          { path: 'llm-costs/chart', redirectTo: 'platform-usage' },
          { path: 'trustx-costs', redirectTo: 'platform-usage' },
          {
            path: 'platform-usage',
            canActivate: [isIndagoAiTenantGuard],
            loadComponent: () => import('./admin/usage/usage-page/usage-page.component').then((it) => it.UsagePageComponent),
          },
          {
            path: 'access-management',
            canActivate: [isIndagoAiTenantGuard],
            loadComponent: () =>
              import('./admin/access-management/access-management.component').then((it) => it.AccessManagementComponent),
          },
          {
            path: 'environment-control',
            canActivate: [isIndagoAiTenantGuard],
            loadComponent: () =>
              import('./admin/environment-control/environment-control.component').then((it) => it.EnvironmentControlComponent),
          },
          {
            path: 'host-allowlist',
            canActivate: [isIndagoAiTenantGuard],
            loadComponent: () =>
              import('./admin/host-allowlist/host-allowlist.component').then((it) => it.HostAllowlistComponent),
          },
          {
            path: 'teams',
            canActivate: [isIndagoAiTenantGuard],
            loadComponent: () => import('./admin/teams/teams.component').then((it) => it.TeamsComponent),
          },
        ],
      },
      {
        path: 'workspaces',
        canActivate: [featureFlagGuard, permissionGuard],
        data: {
          featureFlag: FeatureFlagType.WORKSPACES_ENABLED,
        },
        loadChildren: () => import('./routes/workspaces.routes').then((it) => it.workspacesRoutes),
      },
      {
        path: 'vaults',
        canActivate: [featureFlagGuard, permissionGuard],
        data: {
          featureFlag: FeatureFlagType.VAULT_ENABLED,
          permissionKeys: ['vaults'],
        },
        loadChildren: () => import('./routes/vaults.routes').then((it) => it.vaultsRoutes),
      },
      // The 404 keeps the rail, so it lives in the shell; that is also why `auth` has to match first.
      { path: '**', component: NotFoundComponent },
    ],
  },
];
